// oxlint-disable no-console
/**
 * The Persistence service persists json / text data to a durable storage in the browser
 */

import { IndexedDatabase, type DatabaseStorage } from "./indexed-database";

type Item =
  | {
      type: string;
      value: unknown;
      setAt: number;
    }
  | {
      type: "object";
      value: object;
      setAt: number;
    }
  | {
      type: "number";
      value: number;
      setAt: number;
    }
  | {
      type: "string";
      value: string;
      setAt: number;
    }
  | {
      type: "array";
      value: unknown[];
      setAt: number;
    };

export type PersistenceChangeEvents =
  | {
      type: "change";
      key: string;
      value: unknown;
    }
  | {
      type: "delete";
      key: string;
    };

export type PersistenceEventHandler = (evt: PersistenceChangeEvents) => void;

/**
 * Api wrapper for persistent data storage
 * - this needs to be independent of the underlying tech (indexedBb)
 * - localstorage ran into limits with read/write / storage size
 */
export class PersistentStorage {
  private dbEvents = new EventTarget();

  private readonly storeName: string;
  private readonly database: DatabaseStorage<unknown>;

  constructor(
    args: { storeName: string; database?: DatabaseStorage<unknown> } = {
      storeName: "items",
    },
  ) {
    this.storeName = args.storeName;
    this.get = this.get.bind(this);
    this.set = this.set.bind(this);
    this.delete = this.delete.bind(this);
    this.on = this.on.bind(this);
    this.database =
      args.database ??
      new IndexedDatabase({
        name: "wombat-persistent-storage",
        version: 1,
        upgrade(db) {
          if (!db.objectStoreNames.contains(args.storeName)) {
            db.createObjectStore(args.storeName);
          }
        },
      });
  }

  public async set(
    key: string,
    value: object | string | number | unknown | unknown[],
  ): Promise<void> {
    const _v: Item = {
      type: Array.isArray(value) ? "array" : typeof value,
      value,
      setAt: Date.now(),
    };

    const stored: Item = await this.database.put(this.storeName, _v, key);
    this.dbEvents.dispatchEvent(
      new CustomEvent("persistence-change", {
        detail: { value: stored.value, key, type: "change" },
      }),
    );
  }

  public get<T>(key: string): Promise<T | undefined>;
  public async get(key: string): Promise<unknown> {
    const item: Item | undefined = await this.database.get(this.storeName, key);
    if (item?.type === "number") {
      return Number(item.value);
    }
    return item?.value;
  }

  public async delete(key: string): Promise<void> {
    await this.database.delete(this.storeName, key);
    console.info("PERSIST DATA: value deleted", key);
    this.dbEvents.dispatchEvent(
      new CustomEvent("persistence-change", {
        detail: { key, type: "delete" },
      }),
    );
  }

  public on(eventHandler: PersistenceEventHandler): () => void {
    const persistenceInternalEventHandler = (event: Event) => {
      if (event instanceof CustomEvent) {
        const detail: PersistenceChangeEvents = event.detail;
        eventHandler(detail);
      }
    };
    this.dbEvents.addEventListener(
      "persistence-change",
      persistenceInternalEventHandler,
    );
    return () =>
      this.dbEvents.removeEventListener(
        "persistence-change",
        persistenceInternalEventHandler,
      );
  }
}

const primaryLocalStore = new PersistentStorage();
export default primaryLocalStore;
